/**
 * Hands off the first-paint loader that index.html bakes into the document.
 *
 * The loader is shown before any JavaScript runs. Once the app shell is mounted,
 * the class fades it out and the node is removed after the CSS transition, so the
 * page never flashes blank.
 *
 * The normal trigger is two animation frames after mount, so the freshly
 * rendered shell has painted before the loader leaves. Two frames cannot be the
 * only trigger: rAF does not fire in a background tab, and a starved compositor
 * can stall it for seconds or minutes. If it never fires, the loader would sit
 * on top of a working app and block every interaction. The timer is the
 * fallback. It is generous relative to the normal path (about 32 ms), so a
 * healthy boot never waits on it.
 */

const LOADER_FADE_MS = 300;
const LOADER_FALLBACK_MS = 1500;

export function dismissAppLoader(doc: Document = document): void {
  const loader = doc.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}

/** Schedules one hand-off: after two animation frames, or on the fallback timer. */
export function scheduleAppLoaderDismissal(doc: Document = document): void {
  let dismissed = false;
  const dismiss = () => {
    if (dismissed) {
      return;
    }
    dismissed = true;
    dismissAppLoader(doc);
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(dismiss);
  });
  setTimeout(dismiss, LOADER_FALLBACK_MS);
}
